import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  type InputHTMLAttributes,
  type ReactNode,
} from 'react';
import { cx } from '../cx';
import styles from './Choice.module.css';

// Checkbox — DESIGN_SYSTEM §12 row 5. A native <input type="checkbox"> inside its <label>: the label text is
// the accessible name, the keyboard and forms work by themselves. `indeterminate` is a DOM property, not an
// attribute, so it is applied through a ref.

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  /** "Some of these are on": shown as a bar, exposed as `aria-checked="mixed"`. */
  indeterminate?: boolean;
  testID?: string;
  /** The visible label text — also the accessible name. */
  children: ReactNode;
  /** Class name for the <label> that frames control and text. */
  className?: string;
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
  { indeterminate = false, testID, children, className, disabled, ...rest },
  ref,
) {
  const inner = useRef<HTMLInputElement>(null);
  useImperativeHandle(ref, () => inner.current as HTMLInputElement);
  useEffect(() => {
    if (inner.current) inner.current.indeterminate = indeterminate;
  }, [indeterminate]);
  return (
    <label className={cx(styles.choice, disabled && styles.disabled, className)}>
      <input
        ref={inner}
        type="checkbox"
        className={cx(styles.control, styles.checkbox)}
        disabled={disabled}
        aria-checked={indeterminate ? 'mixed' : undefined}
        data-testid={testID}
        {...rest}
      />
      {children}
    </label>
  );
});
